import { Alert } from '@mui/material';
import { Stack, Typography } from './mui-system';
import type {
  BudgetReason,
  BudgetSource,
  ResourceBudgetView,
} from '../../../api/src/queue/resource-budget.policy';
const names: Record<BudgetSource, string> = {
  queue: 'Очередь',
  donors: 'Доноры',
  materializer: 'Материализатор',
};
const reasons: Record<BudgetReason, string> = {
  granted: 'Резерв выдан',
  idle: 'Готовой работы нет',
  telemetry: 'Нет свежего замера ресурсов',
  pressure: 'Ждём восстановления ресурсов',
  identity: 'Не удалось определить контейнер исполнителя',
  'mode-mismatch': 'Режимы допуска исполнителей различаются',
  slot: 'Заняты все исполнители этого вида',
  turn: 'Раньше поступил другой запрос на ресурсы',
  'memory-budget': 'Недостаточно свободного бюджета памяти',
  'cpu-budget': 'Недостаточно свободного бюджета CPU',
  capacity: 'Задача не помещается в лимиты контейнера',
  storage: 'Недоступно хранилище резервов',
};
const mib = (bytes: number) => Math.round(bytes / 1024 ** 2).toLocaleString('ru-RU');
const cpu = (value: number) => value.toLocaleString('ru-RU', { maximumFractionDigits: 3 });
export function ResourceBudgetInfo({ value }: { value: ResourceBudgetView }) {
  const limits = value.limits;
  return (
    <Stack gap={1} sx={{ mt: 3 }}>
      <Typography variant="h6">Общий бюджет</Typography>
      {value.mode === 'observe' && (
        <Alert severity="info">Режим наблюдения: оценки ресурсов не ограничивают запуск.</Alert>
      )}
      {value.freshness !== 'fresh' && (
        <Alert severity="warning">
          Общий бюджет неизвестен или замер устарел. В режиме защиты новые задачи ожидают свежие
          сведения.
        </Alert>
      )}
      {limits && (
        <>
          <Typography>
            CPU: зарезервировано {cpu(value.reserved.cpu)} из {cpu(limits.cpuBudget)}. Доступно
            контейнеру: {cpu(limits.cpuCapacity)}.
          </Typography>
          <Typography>
            Память: доступно {mib(limits.memoryAvailableBytes)} МиБ; запас для API —{' '}
            {mib(limits.memoryReserveBytes)} МиБ; резервы задач — {mib(value.reserved.memoryBytes)}{' '}
            МиБ.
          </Typography>
        </>
      )}
      {value.active.map((item) => (
        <Typography key={item.source}>
          {names[item.source]}: {item.count} из {value.policy.workloads[item.source].slots}{' '}
          исполнителей · {mib(item.memoryBytes)} МиБ · {limits ? cpu(item.cpu) : '—'} CPU.
          {item.uncertain > 0 ? ' Подтверждение активности задерживается; резерв сохранён.' : ''}
        </Typography>
      ))}
      {value.waiting.map((item, index) => (
        <Alert severity="warning" key={`${item.source}:${index}`}>
          {names[item.source]}:{' '}
          {reasons[value.decisions.find((d) => d.source === item.source)?.reason ?? 'telemetry']}.
          Ожидает с {new Date(item.since).toLocaleTimeString('ru-RU')}.
        </Alert>
      ))}
      <Typography color="text.secondary">
        Резервы — оценки будущего потребления. Они сохраняются до завершения работы. При ожидании
        бюджета попытки заданий не расходуются.
      </Typography>
    </Stack>
  );
}
